'use client'

import Link from 'next/link'
import { useState } from 'react'
import { BookOpen, FileText, Gauge, GraduationCap, Mail, Plus, ShieldCheck, Users, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SheetTitle } from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { UserText } from '@/components/ui/user-text'
import { useTranslation } from '@/lib/i18n'
import { isTerminalFailure } from '@/lib/knowledge/ask-errors'
import type {
  AssistantAvailability,
  AssistantPublic,
  DocumentCitation,
  KnowledgePortalSurface,
  TeamAvailability,
} from '@/lib/knowledge/portal-types'
import { AskComposer, AskThread } from './ask-thread'
import { AssistantWelcome, type WelcomeSuggestion } from './assistant-welcome'
import { ConversationMenu } from './conversation-menu'
import { KnowledgeDocumentViewer, type ViewerTarget } from './document-viewer'
import type { AnswerSession } from './use-answer-session'

export const LIBRARY_HREF = '/portal/library'

/**
 * K2: the team questions a manager is offered, for the team data their
 * assistant may read now (catalogue keys; names off asks for counts).
 */
export function teamQuestionKeys(team: TeamAvailability): string[] {
  const keys = ['knowledgePortal.team.questions.overview']
  if (team.training) keys.push(team.names ? 'knowledgePortal.team.questions.overdueNames' : 'knowledgePortal.team.questions.overdueCount')
  if (team.simulations) keys.push('knowledgePortal.team.questions.simulation')
  if (team.risk) keys.push(team.names ? 'knowledgePortal.team.questions.riskNames' : 'knowledgePortal.team.questions.riskCount')
  if (team.training && team.draftReminders) keys.push('knowledgePortal.team.questions.reminder')
  return keys
}

/** The team questions as welcome cards: each with a short title, an icon and a colour, as the main app's cards have. */
function useTeamSuggestions() {
  const { t } = useTranslation()
  return (team: TeamAvailability | null): WelcomeSuggestion[] =>
    team
      ? teamQuestionKeys(team).map((key): WelcomeSuggestion => {
          const card = { key, module: 'team' as const, question: t(key), typed: false }
          if (key.endsWith('.overview')) return { ...card, title: t('knowledgePortal.team.datasets.overview'), icon: Users, accent: 'text-indigo-500' }
          if (key.includes('.overdue')) return { ...card, title: t('knowledgePortal.team.datasets.training'), icon: GraduationCap, accent: 'text-emerald-500' }
          if (key.endsWith('.simulation'))
            return { ...card, title: t('knowledgePortal.team.datasets.simulations'), icon: ShieldCheck, accent: 'text-fuchsia-500' }
          if (key.includes('.risk')) return { ...card, title: t('knowledgePortal.team.datasets.risk'), icon: Gauge, accent: 'text-rose-500' }
          return { ...card, title: t('knowledgePortal.team.draft.title'), icon: Mail, accent: 'text-amber-500' }
        })
      : []
}

const sameQuestion = (a: string, b: string) => a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase()

export interface AskSessions {
  employee: AnswerSession
  manager: AnswerSession
}

/**
 * The assistant drawer's content, laid out as the main application's drawer
 * (`components/ai-chat/chat-companion.tsx`, drawer mode):
 *
 *   - the header: the "New conversation" dropdown (this person's
 *     conversations: open, delete, search), "+" for a new one, the policy
 *     library, and close;
 *   - for a manager in manager view, "Policies / Team & manager" as Tabs (K2:
 *     the manager surface answers from manager documents and the manager's own
 *     team); each surface keeps its own conversation;
 *   - the hello (robot, greeting, suggestion cards) until the first message,
 *     then the conversation;
 *   - the composer, with the line saying where answers come from and how many
 *     questions are left today.
 *
 * The conversations themselves live in the launcher, so closing the drawer and
 * opening it again keeps them.
 */
export function AskPanel({
  availability,
  surfaces,
  surface,
  onSurfaceChange,
  sessions,
  remaining,
  onClose,
}: {
  availability: AssistantAvailability
  surfaces: KnowledgePortalSurface[]
  surface: KnowledgePortalSurface
  onSurfaceChange: (surface: KnowledgePortalSurface) => void
  sessions: AskSessions
  remaining: Partial<Record<KnowledgePortalSurface, number>>
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [viewer, setViewer] = useState<ViewerTarget | null>(null)
  const assistant = availability[surface]
  if (!assistant) return null
  const session = sessions[surface]
  const firstQuestion = session.entries.find((e) => e.kind === 'question')
  const title = session.title ?? (firstQuestion?.kind === 'question' ? firstQuestion.text : null)

  const body = (
    <SurfaceBody
      assistant={assistant}
      session={session}
      left={remaining[surface] ?? assistant.remainingToday}
      onOpenSource={(c) => setViewer({ sourceId: c.sourceId, title: c.title, page: c.pageFrom })}
    />
  )

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* The drawer's name for screen readers; the header shows the conversation, as the main app's does. */}
      <SheetTitle className="sr-only">
        <UserText>{assistant.name}</UserText>
      </SheetTitle>
      <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border bg-background/80 px-4 backdrop-blur-md">
        <ConversationMenu
          key={surface}
          surface={surface}
          title={title}
          activeId={session.conversationId}
          disabled={session.busy}
          onLoad={session.load}
          onNew={session.reset}
          onDeleted={(id) => {
            if (session.conversationId === id) session.reset()
          }}
        />
        <div className="ms-auto flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label={t('knowledgePortal.panel.newConversation')}
            title={t('knowledgePortal.panel.newConversation')}
            disabled={session.busy}
            onClick={session.reset}
          >
            <Plus className="size-4" aria-hidden="true" />
          </Button>
          <Button asChild variant="ghost" size="icon" className="size-8">
            <Link href={LIBRARY_HREF} aria-label={t('knowledgePortal.panel.library')} title={t('knowledgePortal.panel.library')} onClick={onClose}>
              <BookOpen className="size-4" aria-hidden="true" />
            </Link>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label={t('knowledgePortal.panel.close')}
            title={t('knowledgePortal.panel.close')}
            onClick={onClose}
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </header>

      {surfaces.length > 1 ? (
        <Tabs
          value={surface}
          onValueChange={(next) => onSurfaceChange(next === 'manager' ? 'manager' : 'employee')}
          className="flex min-h-0 flex-1 flex-col gap-0"
        >
          <div className="shrink-0 border-b border-border px-4 py-2">
            <TabsList aria-label={t('knowledgePortal.surface.label')} className="w-full">
              {surfaces.map((s) => (
                <TabsTrigger key={s} value={s}>
                  {s === 'manager' ? t('knowledgePortal.surface.manager') : t('knowledgePortal.surface.employee')}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {surfaces.map((s) => (
            <TabsContent key={s} value={s} className="flex min-h-0 flex-1 flex-col">
              {s === surface ? body : null}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        body
      )}

      <KnowledgeDocumentViewer target={viewer} realm="portal" onClose={() => setViewer(null)} />
    </div>
  )
}

/** One surface: the hello or the conversation, then the composer. */
function SurfaceBody({
  assistant,
  session,
  left,
  onOpenSource,
}: {
  assistant: AssistantPublic
  session: AnswerSession
  left: number
  onOpenSource: (citation: DocumentCitation) => void
}) {
  const { t } = useTranslation()
  const teamSuggestions = useTeamSuggestions()
  const locked = isTerminalFailure(session.lastFailure) || left <= 0
  // "Team & manager" with team answers on: the box asks about policies or the team.
  const team = assistant.surface === 'manager' ? assistant.team : null
  const teamCards = teamSuggestions(team)
  const policyCards = assistant.suggestedQuestions
    // An admin's suggestion that is one of the team questions is offered once, as a team card.
    .filter((q) => !teamCards.some((card) => sameQuestion(card.question, q)))
    .map(
      (q, i): WelcomeSuggestion => ({
        key: `policy-${i}`,
        module: 'policy',
        title: null,
        question: q,
        typed: true,
        icon: FileText,
        accent: 'text-sky-500',
      }),
    )

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {session.entries.length === 0 ? (
          <AssistantWelcome
            name={assistant.name}
            subtitle={
              assistant.greeting ?? (team ? t('knowledgePortal.welcome.subtitleTeam') : t('knowledgePortal.welcome.subtitle'))
            }
            subtitleTyped={assistant.greeting !== null}
            policy={policyCards}
            team={teamCards}
            onAsk={(q) => void session.ask(q)}
            disabled={locked}
          />
        ) : (
          <div data-ask-scroll="" className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            <AskThread
              entries={session.entries}
              label={t('knowledgePortal.panel.conversation')}
              escalation={assistant.escalation}
              onOpenSource={onOpenSource}
              libraryHref={LIBRARY_HREF}
            />
          </div>
        )}
      </div>

      <footer className="shrink-0 border-t border-border bg-background p-4">
        <AskComposer
          variant="drawer"
          onAsk={(q) => void session.ask(q)}
          locked={locked}
          busy={session.busy}
          inputLabel={t('knowledgePortal.panel.inputLabel')}
          placeholder={t(team ? 'knowledgePortal.panel.placeholderTeam' : 'knowledgePortal.panel.placeholder')}
        />
        <p className="mt-2 text-center text-[11px] leading-snug text-muted-foreground">{t('knowledgePortal.panel.disclaimer')}</p>
        <p className="mt-0.5 flex flex-wrap items-center justify-center gap-x-2 text-[10px] text-muted-foreground/80">
          <span>{t('knowledgePortal.panel.remaining', { count: Math.max(0, left) })}</span>
          <span aria-hidden="true" className="hidden sm:inline">
            ·
          </span>
          <span className="hidden sm:inline">{t('knowledgePortal.panel.hint')}</span>
        </p>
      </footer>
    </>
  )
}
